/**
 * Cost Explorer Service
 * Maps to Requirement 77: Web UI — Cost Explorer [MVP]
 * 
 * Provides data fetching and export functionality for cost explorer
 * UPDATED: Now calls real Cost API instead of generating mock data
 * FIXED: Provider filter mapping (cloud -> provider)
 */

import axios from 'axios';
import { authHeaders as sharedAuthHeaders } from './apiHeaders';
import {
  CostExplorerQuery,
  CostExplorerData,
  ExportRequest,
  ExportResponse,
  CostDataPoint,
  CostByDimension,
  CostSummary,
  CostView,
  CostDimension,
  CostFilter,
} from '../types/costExplorer';

// tenantContextMiddleware requires a verified Bearer token on every
// cost-api route now - tenant headers alone are no longer sufficient.
// Delegates to the shared builder so a user Refresh (withFreshData) adds
// Cache-Control: no-cache here too. Ten local copies of this function is why
// the Refresh button worked on some pages and silently did nothing on others.
function authHeaders(): Record<string, string> {
  return sharedAuthHeaders();
}

/**
 * Cost Explorer Service - Calls real Cost API
 */
class CostExplorerService {
  // Base URL for Cost API
  private readonly costApiUrl = import.meta.env.VITE_COST_API_URL 
    ? `${import.meta.env.VITE_COST_API_URL}/api/v1` 
    : 'http://localhost:3002/api/v1';

  /**
   * Maps Cost Explorer filters (dimension + values) to the flat API filter
   * object every /cost/summary and /cost/timeseries call sends. Shared by
   * getCostData and getCostSummary so a fix to this mapping (e.g. the
   * tag:/resource handling below) can't drift between the two - previously
   * getCostSummary had no filter support at all, so drilling into any
   * breakdown row narrowed the chart/breakdown but left the Total
   * Cost/Avg Daily Cost/Projected Monthly tiles showing the unfiltered total.
   */
  private buildApiFilters(filters: CostFilter[]): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {};
    for (const filter of filters) {
      // A drill-down into a "group by tag" or "group by resource" breakdown
      // row (see handleDrillDown in CostExplorerPage.tsx) sets filter.dimension
      // to the literal string "tag:<Key>" or CostDimension.CUSTOM - these
      // aren't real column names, so they need their own mapping to the
      // `tag_<Key>`/`resource_id` filter keys the backend understands
      // (see buildAwsFilterClause/buildGcpFilterClause/applyRecordFilters).
      // Previously these fell through to `filter.dimension.toLowerCase()`
      // (e.g. "tag:costcenter" or "custom"), which matched no real field
      // on any provider, so drilling into a tag or resource row silently
      // returned zero/wrong results.
      let apiField: string;
      if (filter.dimension.startsWith('tag:')) {
        apiField = `tag_${filter.dimension.slice(4)}`;
      } else if (filter.dimension === CostDimension.CUSTOM) {
        apiField = 'resource_id';
      } else {
        const fieldMap: Record<string, string> = {
          'cloud': 'provider',  // CostDimension.CLOUD = 'cloud' (lowercase)
          'account': 'account',
          'service': 'service',
          'region': 'region',
        };
        apiField = fieldMap[filter.dimension] || filter.dimension.toLowerCase();
      }
      result[apiField] = filter.values;
      console.log('🔍 Filter mapping:', {
        dimension: filter.dimension,
        apiField,
        values: filter.values
      });
    }
    return result;
  }

  /**
   * Fetch cost explorer data based on query parameters
   * AC 1: Multi-dimensional group-by
   * AC 2: Time granularity support
   * AC 4: Multiple cost views
   * UPDATED: Now calls real Cost API with filters and cost view
   */
  async getCostData(query: CostExplorerQuery): Promise<CostExplorerData> {
    try {
      console.log('📊 Fetching real cost data from Cost API', { query });

      // Convert dates to ISO format with time
      const startISO = query.startDate.includes('T') ? query.startDate : `${query.startDate}T00:00:00Z`;
      const endISO = query.endDate.includes('T') ? query.endDate : `${query.endDate}T23:59:59Z`;

      // Build filter object from Cost Explorer filters
      const filters = this.buildApiFilters(query.filters || []);

      console.log('📤 Sending filters to API:', filters);

      // Map Cost Explorer cost view to API cost view
      const costViewMap: Record<CostView, string> = {
        [CostView.UNBLENDED]: 'unblended',
        [CostView.BLENDED]: 'blended',
        [CostView.AMORTIZED]: 'amortized',
        // Real amortization now built for AWS (see getCostColumnForView in
        // s3-data.service.ts) - net_amortized/effective use the same CUR
        // formula as amortized since neither actually diverges from it
        // without negotiated enterprise discounts, which neither tenant has.
        [CostView.NET_AMORTIZED]: 'net_amortized',
        [CostView.EFFECTIVE]: 'effective',
        [CostView.NET_UNBLENDED]: 'net_unblended',
      };

      // Map granularity - TimeGranularity enum values are already lowercase
      // ('hourly'/'daily'/'weekly'/'monthly'/'quarterly') and match the backend's
      // expected values directly, except 'quarterly' which the backend doesn't
      // bucket by (s3-data.service.ts/azure/gcp equivalents only support up to
      // monthly) - that one alone falls back to monthly.
      const granularityMap: Record<string, string> = {
        'hourly': 'hourly',
        'daily': 'daily',
        'weekly': 'weekly',
        'monthly': 'monthly',
        'quarterly': 'monthly',
      };
      const granularity = granularityMap[query.granularity] || 'daily';
      
      // Determine currency based on provider selection:
      // - Azure only → INR (native currency, no conversion)
      // - All providers / AWS / GCP → USD (common currency for comparison)
      const providerFromFilter = query.filters?.find(f => f.dimension === 'CLOUD' || f.dimension === 'cloud')?.values?.[0]?.toLowerCase();
      const isAzureOnly = providerFromFilter === 'azure';
      const requestCurrency = isAzureOnly ? 'INR' : 'USD';

      // Req 77 AC1: the "Group By Tag" dropdown sends dimension values like
      // "tag:Owner" (built from the tenant's real cost-allocation tags, see
      // costAllocationTags in CostExplorerPage.tsx) - extract the actual key
      // and pass it as its own param rather than overloading `filter`, since
      // filter narrows rows while this picks which tag's breakdown comes back.
      const selectedDimension = query.groupBy?.dimensions?.[0];
      const groupByTag = selectedDimension?.startsWith('tag:') ? selectedDimension.slice(4) : undefined;
      // Req 77 AC1 "custom dimension" = group by resource ID.
      const groupByResource = selectedDimension === CostDimension.CUSTOM ? true : undefined;

      // Prior comparable period - same duration, immediately preceding the
      // current range (e.g. a 31-day current range compares against the 31
      // days before it) - used to compute each breakdown row's real
      // period-over-period trend. Previously this was hardcoded to
      // {value: 0, direction: 'up'} for every row regardless of actual data.
      const periodMs = new Date(endISO).getTime() - new Date(startISO).getTime();
      const priorEndISO = new Date(new Date(startISO).getTime() - 1000).toISOString();
      const priorStartISO = new Date(new Date(priorEndISO).getTime() - periodMs).toISOString();

      // Fetch summary, time series, and the prior-period summary (for trend
      // calculation) in PARALLEL for faster loading
      const [summaryResponse, timeSeriesResponse, priorSummaryResponse] = await Promise.all([
        axios.get(`${this.costApiUrl}/cost/summary`, {
          params: {
            start: startISO,
            end: endISO,
            currency: requestCurrency,
            cost_view: costViewMap[query.costView],
            filter: Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined,
            group_by_tag: groupByTag,
            group_by_resource: groupByResource,
          },
          headers: authHeaders(),
          timeout: 90000,
        }),
        axios.get(`${this.costApiUrl}/cost/timeseries`, {
          params: {
            start: startISO,
            end: endISO,
            currency: requestCurrency,
            granularity: granularity,
            cost_view: costViewMap[query.costView],
            filter: Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined,
          },
          headers: authHeaders(),
          timeout: 90000,
        }),
        axios.get(`${this.costApiUrl}/cost/summary`, {
          params: {
            start: priorStartISO,
            end: priorEndISO,
            currency: requestCurrency,
            cost_view: costViewMap[query.costView],
            filter: Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined,
            group_by_tag: groupByTag,
            group_by_resource: groupByResource,
          },
          headers: authHeaders(),
          timeout: 90000,
        }).catch((err) => {
          // Trend is a nice-to-have, not core data - a failed prior-period
          // fetch shouldn't break the whole page.
          console.error('Prior-period fetch for trend calculation failed:', err.message);
          return null;
        }),
      ]);

      console.log('✅ Received cost data from API', {
        totalCost: summaryResponse.data.data?.total_cost || 0,
        timeSeriesPoints: timeSeriesResponse.data.data?.length || 0,
        filters: filters,
        costView: costViewMap[query.costView],
        granularity: granularity
      });

      // Transform responses to Cost Explorer format
      return this.transformSummaryResponse(
        summaryResponse.data.data,
        timeSeriesResponse.data.data,
        query,
        priorSummaryResponse?.data?.data ?? null
      );

    } catch (error: any) {
      console.error('❌ Error fetching cost data from API:', error.message);
      console.error('Full error:', error);
      
      // Don't fall back to mock data - throw the error so we can see what's wrong
      throw new Error(`Failed to fetch cost data: ${error.message}`);
    }
  }

  /**
   * Export cost data in specified format
   * AC 3: Support export (CSV, Parquet, FOCUS format)
   */
  async exportData(request: ExportRequest): Promise<ExportResponse> {
    // In production, this would be an API call:
    // const response = await fetch(`${this.baseUrl}/cost-explorer/export`, {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(request),
    // });
    // return response.json();

    // Mock implementation
    return {
      downloadUrl: `/api/v1/exports/mock-export-${Date.now()}.${request.format}`,
      expiresAt: new Date(Date.now() + 3600000).toISOString(), // 1 hour
      fileSize: 1024 * 1024 * 5, // 5 MB
      format: request.format,
    };
  }

  /**
   * Get cost summary metrics
   * UPDATED: Now calls real Cost API with cost view support and provider filter
   * FIXED: Removed duplicate currency conversion - backend already handles it
   */
  async getCostSummary(
    startDate: string,
    endDate: string,
    costView: CostView,
    provider?: string,
    dimensionFilters: CostFilter[] = []
  ): Promise<CostSummary> {
    try {
      console.log('📊 Fetching cost summary from Cost API', { costView, provider, dimensionFilters });

      // Convert dates to ISO format with time
      const startISO = startDate.includes('T') ? startDate : `${startDate}T00:00:00Z`;
      const endISO = endDate.includes('T') ? endDate : `${endDate}T23:59:59Z`;

      // Map Cost Explorer cost view to API cost view
      const costViewMap: Record<CostView, string> = {
        [CostView.UNBLENDED]: 'unblended',
        [CostView.BLENDED]: 'blended',
        [CostView.AMORTIZED]: 'amortized',
        // Real amortization now built for AWS (see getCostColumnForView in
        // s3-data.service.ts) - net_amortized/effective use the same CUR
        // formula as amortized since neither actually diverges from it
        // without negotiated enterprise discounts, which neither tenant has.
        [CostView.NET_AMORTIZED]: 'net_amortized',
        [CostView.EFFECTIVE]: 'effective',
        [CostView.NET_UNBLENDED]: 'net_unblended',
      };

      // Build filter object - provider tab plus any active drill-down
      // filters (tag/resource/service/region/account), same mapping getCostData
      // uses, so the summary tiles narrow along with the chart/breakdown
      // instead of always showing the unfiltered total.
      const filters: Record<string, string | string[]> = this.buildApiFilters(dimensionFilters);
      if (provider && provider !== 'all') {
        filters.provider = [provider];
      }

      // Azure-only → pass INR so backend returns native currency
      // Multi-provider / AWS / GCP → pass USD for comparison
      const requestCurrency = (provider === 'azure') ? 'INR' : 'USD';

      const response = await axios.get(`${this.costApiUrl}/cost/summary`, {
        params: {
          start: startISO,
          end: endISO,
          currency: requestCurrency,
          cost_view: costViewMap[costView],
          filter: Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined,
        },
        headers: authHeaders(),
        timeout: 90000,
      });

      const data = response.data.data;
      const totalCost = data.total_cost || 0;
      const currency = data.currency || 'USD'; // Use backend-provided currency
      
      const days = Math.ceil(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
      );
      const averageDailyCost = totalCost / (days || 1);
      const projectedMonthlyCost = averageDailyCost * 30;

      // Calculate real change by fetching previous period
      const prevEndDate = startDate; // previous period ends where current starts
      const prevStartDate = new Date(new Date(startDate).getTime() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      let changeFromPreviousPeriod = 0;
      let changePercentage = 0;
      // Distinguishes "we confirmed a real 0% change" from "we couldn't get
      // the comparison at all" - previously both looked identical (0%) to
      // the user, which misrepresented a failed/unavailable comparison as
      // "no change happened."
      let changeAvailable = false;

      try {
        const prevStartISO = `${prevStartDate}T00:00:00Z`;
        const prevEndISO = `${prevEndDate}T23:59:59Z`;
        const prevResponse = await axios.get(`${this.costApiUrl}/cost/summary`, {
          params: {
            start: prevStartISO,
            end: prevEndISO,
            currency: requestCurrency,
            cost_view: costViewMap[costView],
            filter: Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined,
          },
          headers: authHeaders(),
          timeout: 30000,
        });
        const prevCost = prevResponse.data.data?.total_cost || 0;
        if (prevCost > 0) {
          changeFromPreviousPeriod = totalCost - prevCost;
          changePercentage = Math.round(((totalCost - prevCost) / prevCost) * 10000) / 100;
          changeAvailable = true;
        }
      } catch (e: any) {
        console.warn('Failed to fetch previous period cost for comparison:', e.message);
      }

      console.log('✅ Cost summary received', { 
        totalCost, 
        averageDailyCost, 
        costView, 
        provider, 
        currency,
        backendCurrency: data.currency 
      });

      return {
        totalCost,
        averageDailyCost,
        projectedMonthlyCost,
        changeFromPreviousPeriod,
        changePercentage,
        changeAvailable,
        currency,
      };
    } catch (error: any) {
      console.error('❌ Error fetching cost summary:', error.message);
      console.error('Full error:', error);

      // Don't fall back to mock data - throw the error
      throw new Error(`Failed to fetch cost summary: ${error.message}`);
    }
  }

  /**
   * Get available values for a dimension (for filter dropdowns)
   * TODO: In future, fetch from API
   */
  async getDimensionValues(dimension: CostDimension): Promise<string[]> {
    // Matches the actual tag value pools the backend assigns per resource
    // group (see ENVIRONMENTS/DEPARTMENTS/APPLICATIONS/COST_CENTERS in
    // mock-cloud-data.service.ts) - these are real filter values, not
    // arbitrary placeholder strings.
    const mockValues: Record<CostDimension, string[]> = {
      [CostDimension.CLOUD]: ['AWS', 'Azure', 'GCP'],
      [CostDimension.ACCOUNT]: ['prod-account-1', 'dev-account-2', 'staging-account-3'],
      [CostDimension.SERVICE]: ['EC2', 'S3', 'RDS', 'Lambda', 'CloudFront', 'EKS'],
      [CostDimension.REGION]: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-southeast-1'],
      [CostDimension.TAG]: ['cost_center:CC1001', 'cost_center:CC1002', 'cost_center:CC1003', 'cost_center:CC1004', 'cost_center:CC1005'],
      [CostDimension.APPLICATION]: ['Platform', 'Data', 'ML', 'Security', 'FinOps'],
      [CostDimension.BUSINESS_UNIT]: ['Engineering', 'Finance', 'Marketing', 'Operations', 'Data'],
      [CostDimension.ENVIRONMENT]: ['production', 'staging', 'development'],
      [CostDimension.CUSTOM]: [],
    };

    return mockValues[dimension] || [];
  }



  /**
   * Transform Cost API summary response to Cost Explorer format
   * Uses the currency returned by the API (INR for Azure-only, USD for multi-provider)
   */
  private transformSummaryResponse(
    summaryData: any,
    timeSeriesData: any[],
    query: CostExplorerQuery,
    priorSummaryData: any = null
  ): CostExplorerData {
    if (!summaryData) {
      return {
        timeSeries: [],
        byDimension: [],
        totalCost: 0,
        currency: 'USD',
        metadata: {
          queryTime: 0,
          dataFreshness: new Date().toISOString(),
          recordCount: 0,
        },
      };
    }

    const totalCost = summaryData.total_cost || 0;
    // Use the currency returned by the API — don't hardcode USD
    const currency = (summaryData.currency as string) || 'USD';
    const breakdown = summaryData.breakdown || {};

    // Get the appropriate breakdown based on the selected dimension - shared
    // between the current period and the prior period (for trend
    // calculation below), so both read the exact same map.
    const dimension = query.groupBy.dimensions[0];
    const extractDimensionData = (b: any): Record<string, number> => {
      if (dimension === CostDimension.SERVICE) return b.by_service || {};
      if (dimension === CostDimension.ACCOUNT) return b.by_account || {};
      if (dimension === CostDimension.REGION) return b.by_region || {};
      if (dimension === CostDimension.CLOUD) return b.by_provider || {};
      if (dimension === CostDimension.ENVIRONMENT) return b.by_environment || {};
      if (dimension === CostDimension.BUSINESS_UNIT) return b.by_business_unit || {};
      if (dimension === CostDimension.APPLICATION) return b.by_application || {};
      if (dimension === CostDimension.TAG) return b.by_tag || {};
      // A specific tag key (e.g. "tag:Owner") picked from the dropdown -
      // distinct from the generic CostDimension.TAG bucket above, which has
      // no particular key. See group_by_tag in getCostData() for the request
      // side of this.
      if (typeof dimension === 'string' && dimension.startsWith('tag:')) return b.by_custom_tag || {};
      if (dimension === CostDimension.CUSTOM) return b.by_resource || {};
      return {};
    };

    const dimensionData = extractDimensionData(breakdown);
    // Real period-over-period comparison - previously this was hardcoded to
    // {value: 0, direction: 'up'} for every row, which always showed a fake
    // "0.0%" with a red up-arrow regardless of what the data actually did.
    const priorDimensionData = priorSummaryData ? extractDimensionData(priorSummaryData.breakdown || {}) : {};

    // Build dimension breakdown
    const byDimension: CostByDimension[] = Object.entries(dimensionData)
      .filter(([, cost]) => cost > 0.01)
      .sort((a, b) => b[1] - a[1])
      .map(([dimensionValue, cost]) => {
        const priorCost = priorDimensionData[dimensionValue] || 0;
        let trend: { value: number; direction: 'up' | 'down' | 'stable' };
        if (!priorSummaryData) {
          // No prior-period data available (fetch failed) - show nothing
          // misleading rather than a fake percentage.
          trend = { value: 0, direction: 'stable' };
        } else if (priorCost <= 0) {
          // Cost exists now but didn't in the prior period at all - a
          // percentage change is undefined (division by zero), not 0%.
          trend = { value: 100, direction: 'up' };
        } else {
          const pctChange = ((cost - priorCost) / priorCost) * 100;
          trend = {
            value: pctChange,
            direction: Math.abs(pctChange) < 0.05 ? 'stable' : pctChange > 0 ? 'up' : 'down',
          };
        }
        return {
          dimensionValue,
          cost,
          percentage: (cost / totalCost) * 100,
          trend,
        };
      });

    // Transform time series data — use currency from response
    const timeSeries: CostDataPoint[] = (timeSeriesData || []).map((point: any) => ({
      timestamp: point.timestamp,
      cost: point.cost,
      currency: point.currency || currency,
      by_provider: point.by_provider,
    }));

    return {
      timeSeries,
      byDimension,
      totalCost,
      currency,  // Use actual currency from API, not hardcoded USD
      metadata: {
        queryTime: 150,
        dataFreshness: new Date().toISOString(),
        recordCount: byDimension.length,
      },
    };
  }




}

export const costExplorerService = new CostExplorerService();
